import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import IQualifyLogo from "./IQualifyLogo";
import FeedbackModal from "./FeedbackModal";
import {
  LayoutDashboard, Users, MessageSquare, AlertTriangle, Search,
  Brain, Upload, Settings, LogOut, Menu, X, TrendingUp, HelpCircle, Activity
} from "lucide-react";

const ALL_NAV_ITEMS = [
  { path: "/", label: "Tổng quan", icon: LayoutDashboard },
  { path: "/employees", label: "Nhân viên", icon: Users },
  { path: "/conversations", label: "Hội thoại", icon: MessageSquare },
  { path: "/heartbeat", label: "Giám sát hoạt động", icon: Activity },
  { path: "/churn", label: "Nguy cơ rời bỏ", icon: AlertTriangle },
  { path: "/issues", label: "Khám phá vấn đề", icon: TrendingUp },
  { path: "/coach", label: "AI Huấn luyện", icon: Brain },
  { path: "/query", label: "Hỏi đáp (NLQ)", icon: HelpCircle },
  { path: "/upload", label: "Tải dữ liệu", icon: Upload },
  { path: "/feedbacks", label: "Đóng góp", icon: MessageSquare },
  { path: "/settings", label: "Cài đặt", icon: Settings },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  // If the user is the limited Admin, show only 4 items. Superadmin sees all.
  const isLimitedAdmin = user?.email?.toLowerCase() === "admin@iqualify.com";
  const navItems = isLimitedAdmin
    ? ALL_NAV_ITEMS.filter((item) =>
        ["/", "/employees", "/conversations", "/heartbeat"].includes(item.path)
      )
    : ALL_NAV_ITEMS;

  return (
    <div className="h-screen overflow-hidden flex">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 ease-in-out ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-20 px-5 border-b border-gray-100">
            <Link to="/" className="flex items-center">
              <IQualifyLogo size="sm" showText={true} />
            </Link>
            <button className="lg:hidden" onClick={() => setSidebarOpen(false)}>
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Nav */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center space-x-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActive
                      ? "bg-blue-900/10 text-blue-900 border-l-[3px] border-blue-900"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  }`}
                >
                  <Icon className={`w-5 h-5 ${isActive ? "text-blue-900" : "text-gray-400"}`} />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* User */}
          <div className="p-4 border-t border-gray-100">
            <div className="flex items-center space-x-3">
              <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{background: 'linear-gradient(135deg, #1e3a8a, #14b8a6)'}}>
                <span className="text-white font-semibold text-sm">
                  {user?.full_name?.charAt(0) || "U"}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{user?.full_name}</p>
                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
              </div>
              <button
                onClick={logout}
                className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors"
                title="Đăng xuất"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-3 sm:px-4 lg:px-8 gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <button className="lg:hidden p-2 hover:bg-gray-100 rounded-lg flex-shrink-0" onClick={() => setSidebarOpen(true)} aria-label="Mở menu">
              <Menu className="w-5 h-5 text-gray-600" />
            </button>
            <div className="relative flex-1 sm:flex-initial">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Tìm kiếm..."
                className="pl-9 sm:pl-10 pr-3 sm:pr-4 py-1.5 sm:py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none w-32 sm:w-64 focus:w-44 sm:focus:w-64 transition-all duration-200"
              />
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            <button
              onClick={() => setFeedbackOpen(true)}
              className="flex items-center space-x-1.5 px-2.5 sm:px-3 py-1.5 text-xs sm:text-sm font-medium text-white shadow-sm rounded-lg hover:shadow-md transition-all hover:-translate-y-0.5"
              style={{ background: "linear-gradient(135deg, #1e3a8a 0%, #14b8a6 100%)" }}
            >
              <MessageSquare className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="hidden xs:inline sm:inline">Góp ý</span>
            </button>
            <span className="badge badge-positive text-xs whitespace-nowrap">{user?.role}</span>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 p-3 sm:p-4 lg:p-8 overflow-y-auto">{children}</main>
      </div>

      {/* Render Feedback Modal on top of everything */}
      <FeedbackModal isOpen={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </div>
  );
}
